import React, { useMemo } from 'react';

import { useGetTunnelMessagesQuery } from 'api/main';
import { texts } from 'utils/languages/fr';

import {
    MemoizedIndexedFormControlLabel,
    MemoizedRadioGroup,
} from '../memoized-components/MemoizedComponents';
import { InfoButton } from 'components';
import { Box, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';

const useStyles = makeStyles(theme => ({
    option: {
        alignItems: 'flex-start',
        marginBottom: '10px',
    },
    label: {
        color: 'rgba(0, 0, 0, 0.54)',
        letterSpacing: 1.6,
    },
}));

const getPoliticallyExposedHelper = (options, intro, numbered = false) => {
    const ListType = numbered ? 'ol' : 'ul';
    return (
        <React.Fragment>
            {intro && (
                <Typography sx={{ marginBottom: '20px' }} gutterBottom>
                    {intro}
                </Typography>
            )}
            <ListType>
                {Object.keys(options).map(i => (
                    <li key={i}>
                        <Typography align="left" gutterBottom>
                            {options[i]}
                        </Typography>
                    </li>
                ))}
            </ListType>
        </React.Fragment>
    );
};

const getPoliticallyExposedMessages = data => {
    return {
        1: data ? data['step5.question_15.option_1'] || false : false,
        2: data ? data['step5.question_15.option_2'] || false : false,
        3: data ? data['step5.question_15.option_3'] || false : false,
    };
};

const politicallyExposedHelperMessages = {
    1: {
        title: texts.POLITICALLY_EXPOSED_OPTION_1_HELPER_TITLE,
        content: texts.POLITICALLY_EXPOSED_OPTION_1_HELPER_CONTENT,
        numbered: false,
    },
    2: {
        title: texts.POLITICALLY_EXPOSED_OPTION_2_HELPER_TITLE,
        content: texts.POLITICALLY_EXPOSED_OPTION_2_HELPER_CONTENT,
        numbered: true,
    },
    3: {
        title: texts.POLITICALLY_EXPOSED_OPTION_3_HELPER_TITLE,
        content: texts.POLITICALLY_EXPOSED_OPTION_3_HELPER_CONTENT,
        numbered: true,
    },
};

const PoliticallyExposedForm = ({ politicallyExposed, onChange, onChangeReason, row = false }) => {
    const classes = useStyles();

    const { data: messagesData } = useGetTunnelMessagesQuery();
    const messages = useMemo(() => getPoliticallyExposedMessages(messagesData), [messagesData]);

    if (!politicallyExposed) {
        return null;
    }

    return (
        <React.Fragment>
            <Box display="flex" alignItems="center">
                <Typography className={classes.label}>
                    {texts.ASK_IF_POLITICALLY_EXPOSED}
                </Typography>
                <InfoButton
                    disableMarginBottom
                    title={texts.POLITICALLY_EXPOSED_INFO_TITLE}
                    content={getPoliticallyExposedHelper(
                        [
                            texts.POLITICALLY_EXPOSED_INFO_CONTENT_2,
                            texts.POLITICALLY_EXPOSED_INFO_CONTENT_3,
                        ],
                        texts.POLITICALLY_EXPOSED_INFO_CONTENT_1
                    )}
                />
            </Box>
            <MemoizedRadioGroup
                name="politicallyExposedPerson"
                value={politicallyExposed.politicallyExposedPerson}
                onChange={onChange}
                yesValue="true"
                noValue="false"
                yesLabel={texts.OUI}
                noLabel={texts.NON}
                row={row}
            />
            <Box ml="20px" mb="30px">
                {politicallyExposed.politicallyExposedPerson === 'true' &&
                    Object.keys(messages).map(i => (
                        <Box display="flex" alignItems="start" key={i}>
                            <MemoizedIndexedFormControlLabel
                                index={i}
                                name={i}
                                className={classes.option}
                                label={messages[i]}
                                value={politicallyExposed.politicallyExposedReason[i]}
                                checked={politicallyExposed.politicallyExposedReason[i] === true}
                                onChange={onChangeReason}
                            />
                            <InfoButton
                                disableMarginBottom
                                title={politicallyExposedHelperMessages[i].title}
                                content={getPoliticallyExposedHelper(
                                    politicallyExposedHelperMessages[i].content,
                                    null,
                                    politicallyExposedHelperMessages[i].numbered
                                )}
                            />
                        </Box>
                    ))}
            </Box>
        </React.Fragment>
    );
};

export default PoliticallyExposedForm;
